/**
 * The stand-in server for Beu Messages until the backend lands.
 *
 * It keeps a small team chat in memory (and in localStorage, keyed by salon
 * and user, so a refresh keeps your test messages), answers the same calls
 * the real API will (see api.js for the contract) and plays the other side:
 * teammates read what you send, sometimes react, and now and then type a
 * reply. Everything it emits goes through subscribe(), exactly as a socket
 * would, so the screens never know the difference.
 *
 * Not for production: api.js only picks this while MESSAGES_VISIBLE is on a
 * non-live environment.
 */
import { localId, toggleReaction, findMentions, firstName, channelName, firstUrl, linkPreview, replyChannel } from './model';
import { CLIENTS, seedClients, OLA_CALL, TEXTING_CLIENTS, textingChats } from './seedClients';
import { withSupport, createSupportDesk } from './mockSupport';
import { baileyAnswer } from './baileyTeam';

const STORE_VERSION = 1;
const MAX_PER_CONVERSATION = 300;
const MAX_PERSISTED_IMAGE = 180 * 1024; // bigger photos are kept for the session only

const TEAM = [
    { id: 'tm-jade', name: 'Jade Miller', role: 'Senior stylist' },
    { id: 'tm-sophie', name: 'Sophie Carter', role: 'Colourist' },
    { id: 'tm-marcus', name: 'Marcus Reid', role: 'Barber' },
    { id: 'tm-priya', name: 'Priya Shah', role: 'Nail technician' },
    { id: 'tm-leah', name: 'Leah Okafor', role: 'Front of house' },
];

/** Canned replies by mood, so the simulated team sounds like a salon. */
const REPLIES = {
    thanks: ['Thank you!', 'Legend, thanks', 'Perfect, thank you 🙏', 'You star'],
    yes: ['Yep, on it', 'No problem', 'Sure, leave it with me', 'Done 👍'],
    later: ['With a client, will look after', 'Give me 10 mins', 'Just finishing a colour, back shortly'],
    question: ['Good question, let me check the diary', 'I think so, will confirm', 'Not sure, ask Leah?'],
};

function identity() {
    let user = {};
    try { user = JSON.parse(localStorage.getItem('user')) || {}; } catch (e) { user = {}; }
    const id = String(localStorage.getItem('userId') || user._id || 'me');
    const salonId = String(localStorage.getItem('salonId') || user.salonId || 'salon');
    const name = [user.first_name || user.firstName, user.last_name || user.lastName].filter(Boolean).join(' ')
        || user.name || 'You';
    return { id, salonId, name };
}

const minsAgo = (n) => new Date(Date.now() - n * 60000).toISOString();
const minsFromNow = (n) => new Date(Date.now() + n * 60000).toISOString();

/** A little SVG "photo" so the seed has a real image to open full screen. */
export function placeholderPhoto(label, a, b) {
    const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="640" height="480"><defs><linearGradient id="g" x1="0" y1="0" x2="1" y2="1"><stop offset="0" stop-color="${a}"/><stop offset="1" stop-color="${b}"/></linearGradient></defs><rect width="640" height="480" fill="url(#g)"/><text x="320" y="250" font-family="Helvetica, Arial" font-size="26" fill="rgba(255,255,255,.75)" text-anchor="middle">${label}</text></svg>`;
    return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
}

/** Where the team is today, from the (stand-in) diary: always relative to now. */
function todaysStatuses(people) {
    const next = { ...people };
    const set = (id, status) => { if (next[id]) next[id] = { ...next[id], status }; };
    set('tm-jade', { state: 'free' });
    set('tm-sophie', { state: 'client', until: minsFromNow(85) });
    set('tm-marcus', { state: 'client', until: minsFromNow(25) });
    set('tm-priya', { state: 'off' });
    set('tm-leah', { state: 'break', until: minsFromNow(12) });
    return next;
}

/** A booking snapshot for a cover request, a day or so from now. */
function coverBooking(daysAhead, h, mins, id, client, services) {
    const d = new Date();
    d.setDate(d.getDate() + daysAhead);
    d.setHours(h, mins, 0, 0);
    const DAYS = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'];
    const pad = (n) => (n < 10 ? `0${n}` : n);
    return { type: 'booking', id, client, services, dayShort: DAYS[d.getDay()], dayNum: d.getDate(), when: `${DAYS[d.getDay()].charAt(0)}${DAYS[d.getDay()].slice(1).toLowerCase()} ${pad(h)}:${pad(mins)}` };
}

function seed(me) {
    let people = {};
    people[me.id] = { id: me.id, name: me.name, role: 'You', status: { state: 'free' } };
    TEAM.forEach((p) => { people[p.id] = { ...p, status: { state: 'free' } }; });
    people = todaysStatuses(people);

    const everyone = [me.id].concat(TEAM.map((p) => p.id));
    const conv = (id, extra) => ({
        id, space: 'team', memberIds: everyone, pinned: false, muted: false,
        createdAt: minsAgo(60 * 24 * 30), readUpTo: {}, ...extra,
    });
    const conversations = {
        'c-foh': conv('c-foh', { kind: 'channel', name: 'front-of-house', topic: 'Reception and the floor', pinned: true, everyone: true }),
        'c-colour': conv('c-colour', { kind: 'channel', name: 'colour-bar', topic: 'Stock, formulas and colour questions' }),
        'c-news': conv('c-news', { kind: 'channel', name: 'announcements', topic: 'News for the whole team', muted: true, postBy: 'managers' }),
        'd-jade': conv('d-jade', { kind: 'dm', memberIds: [me.id, 'tm-jade'] }),
        'd-marcus': conv('d-marcus', { kind: 'dm', memberIds: [me.id, 'tm-marcus'] }),
        'g-weekend': conv('g-weekend', { kind: 'group', memberIds: [me.id, 'tm-sophie', 'tm-leah'] }),
    };

    const m = (id, conversationId, authorId, ago, text, extra = {}) => ({
        id, conversationId, authorId, createdAt: minsAgo(ago), text,
        attachments: [], reactions: {}, mentions: [], ...extra,
    });
    const day = 60 * 24;
    const messages = {
        'c-foh': [
            m('f1', 'c-foh', 'tm-leah', day + 300, 'Till is counted and balanced, float is in the safe'),
            m('f2', 'c-foh', 'tm-marcus', day + 280, "We're low on 7N, I've put it on the list", { reactions: { '🙌': ['tm-leah'] } }),
            m('f3', 'c-foh', 'tm-jade', day + 120, 'Can anyone take my 4pm on Thursday? School run'),
            m('f4', 'c-foh', 'tm-leah', day + 118, "I've got it, I'll swap my lunch", { reactions: { '❤️': ['tm-jade'], '🙏': ['tm-jade', 'tm-sophie'] } }),
            m('f5', 'c-foh', 'tm-jade', 172, "Morning all. Anika just moved her 2pm to Friday, is Sophie free then?", {
                card: { type: 'booking', id: 'demo-booking-anika', client: 'Anika Patel', services: 'Balayage, Cut and finish', when: 'Fri 14:00', stylist: 'Sophie', status: 'Moved', dayShort: 'FRI', dayNum: 17 },
                reactions: { '👀': ['tm-sophie'] },
            }),
            m('f6', 'c-foh', 'tm-sophie', 168, "Yes, I'm free. Popped it in"),
            m('f7', 'c-foh', 'tm-sophie', 167, 'Thanks Jade', { reactions: { '👍': [me.id, 'tm-jade'] } }),
            m('f8', 'c-foh', 'tm-marcus', 130, "Walk-in colour consult at 12. Here's the inspo she sent", {
                attachments: [{ type: 'image', url: placeholderPhoto('Inspiration photo', '#6b5240', '#c49a6c'), width: 640, height: 480 }],
                reactions: { '😍': ['tm-jade', 'tm-priya', 'tm-sophie'] },
            }),
            m('f9', 'c-foh', 'tm-priya', 95, '', { attachments: [{ type: 'gif', url: '', title: 'You are a star', width: 220, height: 160, provider: 'KLIPY' }] }),
            m('f10', 'c-foh', me.id, 60, 'Lunch cover sorted: Leah is on reception 12 to 1', { status: 'sent', reactions: { '🙏': ['tm-leah', 'tm-jade'] } }),
            m('f11', 'c-foh', 'tm-jade', 14, '@' + me.name.split(' ')[0] + ' can you check the card reader? It dropped out twice this morning', { mentions: [me.id] }),
            m('f12', 'c-foh', 'tm-sophie', 8, 'Can anyone take my 17:30 tomorrow? Dentist 🙈', {
                card: { type: 'cover', status: 'open', booking: coverBooking(1, 17, 30, 'demo-cover-1', 'Hannah Lee', 'Cut and finish') },
            }),
        ],
        'c-colour': [
            m('k1', 'c-colour', 'tm-sophie', day + 60, 'Formula for Hannah: 7/1 + 8/0, 20 vol, 35 mins'),
            m('k2', 'c-colour', 'tm-priya', 40, 'Toner stock is low, ordering on Friday'),
            m('k3', 'c-colour', 'tm-marcus', 20, 'Can someone grab me a bowl and brush from the back?'),
        ],
        'c-news': [
            m('n1', 'c-news', me.id, day * 3, 'Team night out has moved to Friday the 24th 🎉', { status: 'sent', reactions: { '🎉': ['tm-jade', 'tm-leah', 'tm-priya'] } }),
        ],
        'd-jade': [
            m('j1', 'd-jade', me.id, day + 30, 'Thanks for covering yesterday', { status: 'sent' }),
            m('j2', 'd-jade', 'tm-jade', day + 25, 'Any time!'),
            m('j3', 'd-jade', 'tm-jade', 9, 'Can you cover my 4pm? School run'),
        ],
        'd-marcus': [
            m('r1', 'd-marcus', 'tm-marcus', day + 200, 'Can I leave 30 mins early on Friday?'),
            m('r2', 'd-marcus', me.id, day + 190, 'Yes, see you tomorrow', { status: 'sent' }),
        ],
        'g-weekend': [
            m('w1', 'g-weekend', 'tm-leah', day * 2, 'Swapping Saturdays works for me'),
            m('w2', 'g-weekend', 'tm-sophie', day * 2 - 5, 'Perfect, thank you both'),
        ],
    };
    // Thread on Sophie's "Yes, I'm free".
    messages['c-foh'].push(
        m('t1', 'c-foh', 'tm-jade', 166, 'Legend, thank you', { threadRootId: 'f6' }),
        m('t2', 'c-foh', 'tm-marcus', 150, 'Can you add a toner? She asked last time', { threadRootId: 'f6' }),
        m('t3', 'c-foh', 'tm-sophie', 148, 'Done, added 15 mins on the end', { threadRootId: 'f6' }),
    );

    // Everything older than the last hour is read, so the inbox opens with a
    // couple of unread chats, like a real morning.
    Object.keys(conversations).forEach((id) => {
        const list = messages[id] || [];
        const last = list.filter((x) => !x.threadRootId).slice(-1)[0];
        conversations[id].lastMessageAt = last ? last.createdAt : conversations[id].createdAt;
        const readMark = id === 'c-foh' ? minsAgo(30) : id === 'd-jade' ? minsAgo(20) : id === 'c-colour' ? minsAgo(45) : minsAgo(0);
        conversations[id].readUpTo = everyone.reduce((acc, uid) => { acc[uid] = uid === me.id ? readMark : minsAgo(0); return acc; }, {});
    });
    return { people, conversations, messages };
}

/**
 * A brand-new salon's first day in Messages: the team from Beu, #general
 * with everyone in it, #announcements for managers, and nothing else yet.
 * What the server makes when a salon first opens Messages (backend brief, section 26).
 */
export function freshSeed(me) {
    let people = {};
    people[me.id] = { id: me.id, name: me.name, role: 'You', status: { state: 'free' } };
    TEAM.forEach((p) => { people[p.id] = { ...p, status: { state: 'free' } }; });
    people = todaysStatuses(people);
    const everyone = [me.id].concat(TEAM.map((p) => p.id));
    const at = new Date().toISOString();
    const read = everyone.reduce((acc, uid) => { acc[uid] = at; return acc; }, {});
    const channel = (id, extra) => ({
        id, space: 'team', kind: 'channel', memberIds: everyone, pinned: false, muted: false, everyone: true,
        createdAt: at, lastMessageAt: at, readUpTo: read, ...extra,
    });
    const line = (id, conversationId, text) => ({ id, conversationId, authorId: 'system', createdAt: at, text: '', attachments: [], reactions: {}, mentions: [], system: { text } });
    return {
        fresh: true,
        people,
        conversations: {
            'c-general': channel('c-general', { name: 'general', topic: 'Everyone at the salon', pinned: true }),
            'c-news': channel('c-news', { name: 'announcements', topic: 'News for the whole team', postBy: 'managers' }),
        },
        messages: {
            'c-general': [line('g0', 'c-general', 'Everyone at the salon is in #general')],
            'c-news': [line('a0', 'c-news', 'Only managers post here. Everyone can read and react')],
        },
    };
}

/** Add the Clients space (and Bailey) to a team-only store, new or saved. */
function withClients(db, me) {
    const hasClients = Object.keys(db.conversations).some((id) => db.conversations[id].space === 'clients');
    db.people.bailey = { id: 'bailey', name: 'Bailey', kind: 'bailey', role: 'Your assistant', status: { state: 'free' } };
    CLIENTS.concat(TEXTING_CLIENTS).forEach((c) => { db.people[c.id] = { ...c, status: { state: 'free' } }; });
    Object.keys(db.people).forEach((id) => { if (!db.people[id].kind) db.people[id] = { ...db.people[id], kind: 'staff' }; });
    if (hasClients || db.fresh) return db;
    const extra = seedClients(me.id);
    Object.keys(extra.messages).forEach((cid) => {
        extra.messages[cid] = extra.messages[cid].map((msg) => (msg.attachments.some((a) => a.demoTone) ? {
            ...msg, attachments: msg.attachments.map((a) => (a.demoTone ? { ...a, url: placeholderPhoto(a.title || 'Photo', a.demoTone[0], a.demoTone[1]) } : a)),
        } : msg));
    });
    Object.keys(extra.conversations).forEach((cid) => {
        const c = extra.conversations[cid];
        const list = extra.messages[cid];
        const last = list.filter((x) => !x.threadRootId && !x.note).slice(-1)[0];
        c.lastMessageAt = last ? last.createdAt : c.createdAt;
        const unreadFor = { 'k-anika': 26, 'k-grace': 40, 'k-tom': 60 };
        c.readUpTo = { [me.id]: unreadFor[cid] ? minsAgo(unreadFor[cid]) : minsAgo(0) };
    });
    return {
        ...db,
        conversations: { ...db.conversations, ...extra.conversations },
        messages: { ...db.messages, ...extra.messages },
    };
}

/** Bailey's state, channels and the call card for saved demos made before one client inbox. */
function upgradeClientChats(db, me) {
    const set = (id, patch) => { if (db.conversations[id] && !db.conversations[id].channelled) db.conversations[id] = { ...db.conversations[id], ...patch, channelled: true }; };
    if (db.conversations['k-grace'] && !db.conversations['k-grace'].channelled && db.messages['k-grace']) {
        const line = { id: 'g3', conversationId: 'k-grace', authorId: 'system', createdAt: minsAgo(31), text: '', attachments: [], reactions: {}, mentions: [], system: { text: 'Bailey handed this to the team: a colour question for a stylist' } };
        if (!db.messages['k-grace'].some((m) => m.id === 'g3')) db.messages['k-grace'] = db.messages['k-grace'].concat(line);
    }
    set('k-grace', { channel: 'booking-page', bailey: { state: 'needs-you', since: minsAgo(31) } });
    set('k-ruth', { channel: 'booking-page', bailey: { state: 'replying', since: minsAgo(60 * 24 + 399) } });
    if (db.conversations['k-ola'] && !db.conversations['k-ola'].channelled) {
        set('k-ola', { channel: 'app', bailey: { state: 'paused', takenBy: 'tm-priya', since: minsAgo(60 * 24 + 192) } });
        const call = OLA_CALL();
        if (db.messages['k-ola'] && !db.messages['k-ola'].some((m) => m.id === call.id)) db.messages['k-ola'] = [call].concat(db.messages['k-ola']);
    }
    ['k-anika', 'k-tom'].forEach((id) => set(id, { channel: id === 'k-tom' ? 'sms' : 'app' }));
    // Saved demos: Hannah's email has a subject (14 Oct 2026)
    if (db.messages['k-hannah']) db.messages['k-hannah'] = db.messages['k-hannah'].map((x) => (x.id === 'hn1' && !x.subject ? { ...x, subject: "Thursday's cut" } : x));
    // Megan (replied STOP) and Hannah (email only) for the texting rules, 14 Oct 2026
    if (db.conversations['k-anika'] && !db.conversations['k-megan']) {
        const extra = textingChats(me.id);
        Object.keys(extra.conversations).forEach((id) => { db.conversations[id] = extra.conversations[id]; db.messages[id] = extra.messages[id]; });
    }
}

function storageKey(me) { return `bm:v${STORE_VERSION}:${me.salonId}:${me.id}`; }

function load(me) {
    try {
        const raw = localStorage.getItem(storageKey(me));
        if (!raw) return null;
        const data = JSON.parse(raw);
        return data && data.conversations && data.messages && data.people ? data : null;
    } catch (e) { return null; }
}

function persistable(message) {
    if (!message.attachments || !message.attachments.length) return message;
    return {
        ...message,
        attachments: message.attachments.map((a) => (a.url && a.url.indexOf('blob:') === 0) || (a.url && a.url.length > MAX_PERSISTED_IMAGE)
            ? { ...a, url: '', expired: true } : a),
    };
}

export function createMockAdapter(options = {}) {
    const me = options.identity || identity();
    const delay = options.delay == null ? 1 : options.delay; // 0 in tests
    /** Seed or saved data, with every space, everyone's day as of now, and mine. */
    const build = (base) => {
        const next = withSupport(withClients(base, me), me);
        next.people = todaysStatuses(next.people);
        // My own day, as the diary would give it: free now, a client in a while.
        next.people[me.id] = {
            ...(next.people[me.id] || {}), id: me.id, name: me.name, role: 'You', status: { state: 'free' },
            diary: { nextClientAt: minsFromNow(40), nextClientEndsAt: minsFromNow(95), nextClientName: 'Hannah Lee' },
        };
        // Saved demos from before Bailey joined client chats (13 Oct 2026)
        upgradeClientChats(next, me);
        // Saved demos from before channel rules (13 Oct 2026)
        if (next.conversations['c-foh'] && next.conversations['c-foh'].everyone === undefined) next.conversations['c-foh'] = { ...next.conversations['c-foh'], everyone: true };
        if (next.conversations['c-news'] && next.conversations['c-news'].postBy === undefined) next.conversations['c-news'] = { ...next.conversations['c-news'], postBy: 'managers' };
        next.reminders = next.reminders || [];
        next.saved = next.saved || [];
        next.channelOrder = next.channelOrder || [];
        next.prefs = next.prefs || { pausedUntil: null, workingOnly: true };
        next.clientSettings = next.clientSettings || {
            awayOn: false,
            awayText: "Thanks for your message! We're closed right now and will reply as soon as we open.",
        };
        next.quickReplies = next.quickReplies || [
            { id: 'qr-late', title: 'Running late', text: "Hi {first name}, we're running about 10 minutes behind today. Sorry! See you soon" },
            { id: 'qr-park', title: 'Parking', text: 'Park Lane car park is free after 6pm and all day Sunday, two minutes from us' },
            { id: 'qr-after', title: 'Colour aftercare', text: 'Hi {first name}, wait 48 hours before washing, use a colour-safe shampoo and keep the heat low. Any questions, just message us' },
            { id: 'qr-deposit', title: 'Deposits', text: 'We take a deposit for colour and longer services. It comes off your bill on the day' },
            { id: 'qr-patch', title: 'Patch test', text: "Hi {first name}, you'll need a quick patch test at least 48 hours before your colour. Pop in any time, it takes 5 minutes" },
            { id: 'qr-thanks', title: 'Thank you', text: 'Thank you {first name}, see you soon! {my name}' },
        ];
        return next;
    };
    let db = build((!options.fresh && load(me)) || seed(me));
    const listeners = new Set();
    const timers = new Set();
    const lastSimulated = {};

    const emit = (event) => listeners.forEach((fn) => { try { fn(event); } catch (e) { /* a broken listener never stops the others */ } });
    const later = (ms, fn) => {
        const t = setTimeout(() => { timers.delete(t); fn(); }, ms * delay);
        timers.add(t);
    };
    const save = () => {
        try {
            const trimmed = { ...db, messages: {} };
            Object.keys(db.messages).forEach((id) => { trimmed.messages[id] = db.messages[id].slice(-MAX_PER_CONVERSATION).map(persistable); });
            localStorage.setItem(storageKey(me), JSON.stringify(trimmed));
        } catch (e) { /* storage full or blocked: the session still works */ }
    };
    const findMessage = (conversationId, id) => (db.messages[conversationId] || []).find((x) => x.id === id);
    const replaceMessage = (next) => {
        db.messages[next.conversationId] = db.messages[next.conversationId].map((x) => (x.id === next.id ? next : x));
        save();
        emit({ type: 'message-updated', message: next });
    };
    const touch = (conversationId, at, fromClient) => {
        const c = db.conversations[conversationId];
        if (!c) return;
        // A client writing again brings a Done chat back
        db.conversations[conversationId] = { ...c, lastMessageAt: at, ...(fromClient && c.doneAt ? { doneAt: null } : {}) };
        emit({ type: 'conversation', conversation: db.conversations[conversationId] });
    };

    /** A reminder rings when it is due (the real backend sends a push). */
    function armReminder(r) {
        if (r.done || r.due) return;
        const wait = new Date(r.dueAt).getTime() - Date.now();
        later(Math.max(0, Math.min(wait, 2147483000)) / (delay || 1), () => {
            const current = db.reminders.find((x) => x.id === r.id);
            if (!current || current.done) return;
            current.due = true;
            save();
            emit({ type: 'reminder', reminder: { ...current } });
        });
    }

    /** A message held until someone is free arrives on time. */
    function armDelivery(message) {
        const wait = new Date(message.deliverAt).getTime() - Date.now();
        later(Math.max(0, Math.min(wait, 2147483000)) / (delay || 1), () => {
            const current = findMessage(message.conversationId, message.id);
            if (current) emit({ type: 'message-updated', message: { ...current } });
        });
    }

    const updateCard = (conversationId, messageId, patch, systemText) => {
        const current = findMessage(conversationId, messageId);
        if (!current || !current.card) return;
        replaceMessage({ ...current, card: { ...current.card, ...patch } });
        if (systemText) {
            const line = { id: localId('srv'), conversationId, authorId: 'system', createdAt: new Date().toISOString(), text: '', attachments: [], reactions: {}, mentions: [], system: { text: systemText } };
            db.messages[conversationId] = db.messages[conversationId].concat(line);
            save();
            emit({ type: 'message', message: line });
        }
    };

    /** A client on the other end: reads, pays, picks a time, fills the form, says thanks. */
    /** Bailey's state in a client chat, with a quiet line saying who changed it. */
    function setBailey(conversationId, patch, lineText) {
        const c = db.conversations[conversationId];
        if (!c) return null;
        const at = new Date().toISOString();
        db.conversations[conversationId] = { ...c, bailey: { ...(c.bailey || {}), ...patch, since: at } };
        const line = { id: localId('srv'), conversationId, authorId: 'system', createdAt: at, text: '', attachments: [], reactions: {}, mentions: [], system: { text: lineText } };
        db.messages[conversationId] = (db.messages[conversationId] || []).concat(line);
        save();
        emit({ type: 'conversation', conversation: db.conversations[conversationId] });
        emit({ type: 'message', message: line });
        return db.conversations[conversationId];
    }

    function simulateClient(sent) {
        const c = db.conversations[sent.conversationId];
        if (!c || sent.note) return;
        if (sent.deliverAt && new Date(sent.deliverAt) > new Date()) return; // they can't see it yet
        const client = db.people[c.clientId];
        const name = firstName(client && client.name);
        later(1500, () => {
            db.conversations[c.id] = { ...db.conversations[c.id], readUpTo: { ...db.conversations[c.id].readUpTo, [c.clientId]: new Date().toISOString() } };
            save();
            emit({ type: 'read', conversationId: c.id, userId: c.clientId, at: db.conversations[c.id].readUpTo[c.clientId] });
        });
        const card = sent.card;
        if (card && card.type === 'slots' && card.options && card.options.length) {
            later(5000, () => updateCard(c.id, sent.id, { picked: card.options[0].when }, `${name} booked ${card.options[0].when} from your times`));
            return;
        }
        if (card && card.type === 'deposit') {
            later(6000, () => updateCard(c.id, sent.id, { status: 'paid', paidAt: new Date().toISOString(), cardEnding: '4417' }, `${name} paid the £${card.amount} deposit`));
            return;
        }
        if (card && card.type === 'form') {
            later(3000, () => updateCard(c.id, sent.id, { status: 'opened' }));
            later(9000, () => updateCard(c.id, sent.id, { status: 'completed', completedAt: new Date().toISOString(), flagged: card.formId === 'f-consult' ? 1 : 0 }, `${name} completed ${card.name}`));
            return;
        }
        // A text from Beu's shared number (no business number) or an email with no
        // reply address can't be answered: the client never writes back here
        const reach = (client && client.reach) || {};
        if (sent.channel === 'sms' && reach.repliesCome === false) return;
        if (sent.channel === 'email' && !reach.emailRepliesCome) return;
        if (Math.random() < 0.6) {
            later(3500, () => emit({ type: 'typing', conversationId: c.id, userId: c.clientId, on: true }));
            later(6000, () => {
                emit({ type: 'typing', conversationId: c.id, userId: c.clientId, on: false });
                const reply = { id: localId('srv'), conversationId: c.id, authorId: c.clientId, createdAt: new Date().toISOString(), text: ['Thank you so much!', 'Perfect, thanks', 'Lovely, see you then 😊'][Math.floor(Math.random() * 3)], attachments: [], reactions: {}, mentions: [], channel: sent.channel || undefined };
                db.messages[c.id] = db.messages[c.id].concat(reply);
                save();
                emit({ type: 'message', message: reply });
                touch(c.id, reply.createdAt, true);
            });
        }
    }

    /** "@Bailey …" in a team chat: Bailey types, then answers in the chat. */
    function simulateBailey(sent, c) {
        later(600, () => emit({ type: 'typing', conversationId: c.id, userId: 'bailey', on: true }));
        later(1800, () => {
            emit({ type: 'typing', conversationId: c.id, userId: 'bailey', on: false });
            const text = baileyAnswer({ text: sent.text, people: db.people, meId: me.id, messages: (db.messages[c.id] || []).filter((m) => m.id !== sent.id) });
            const reply = { id: localId('srv'), conversationId: c.id, authorId: 'bailey', createdAt: new Date().toISOString(), text, attachments: [], reactions: {}, mentions: [], replyToId: sent.id };
            db.messages[c.id] = (db.messages[c.id] || []).concat(reply);
            save();
            emit({ type: 'message', message: reply });
            touch(c.id, reply.createdAt);
        });
    }

    /** Someone takes a cover request I posted, after a moment. */
    function simulateCover(sent, c) {
        const free = c.memberIds.filter((id) => id !== me.id && db.people[id] && db.people[id].status && db.people[id].status.state === 'free');
        const taker = free[0];
        if (!taker) return;
        later(2500, () => emit({ type: 'typing', conversationId: c.id, userId: taker, on: true }));
        later(6000, () => {
            emit({ type: 'typing', conversationId: c.id, userId: taker, on: false });
            coverTaken(c.id, sent.id, taker);
        });
    }

    function coverTaken(conversationId, messageId, takerId) {
        const current = findMessage(conversationId, messageId);
        if (!current || !current.card || current.card.status !== 'open') return null;
        replaceMessage({ ...current, card: { ...current.card, status: 'taken', takenBy: takerId, takenAt: new Date().toISOString() } });
        const b = current.card.booking || {};
        const who = takerId === me.id ? firstName(me.name) : firstName((db.people[takerId] || {}).name);
        const line = { id: localId('srv'), conversationId, authorId: 'system', createdAt: new Date().toISOString(), text: '', attachments: [], reactions: {}, mentions: [],
            system: { text: `${who} is taking ${b.client ? `${firstName(b.client)}'s ` : ''}${b.when || 'booking'}` } };
        db.messages[conversationId] = db.messages[conversationId].concat(line);
        save();
        emit({ type: 'message', message: line });
        return true;
    }

    /** The other side: read it, maybe react, sometimes reply. */
    function simulateTeam(sent) {
        const c = db.conversations[sent.conversationId];
        if (!c || sent.threadRootId) return;
        if (sent.card && sent.card.type === 'cover') { simulateCover(sent, c); return; }
        if ((sent.mentions || []).indexOf('bailey') !== -1) { simulateBailey(sent, c); return; }
        if (c.space === 'clients') { simulateClient(sent); return; }
        if (c.space === 'support') { support.simulate(sent); return; }
        if (sent.deliverAt && new Date(sent.deliverAt) > new Date()) return; // they can't see it yet
        const others = c.memberIds.filter((id) => id !== me.id && db.people[id] && db.people[id].status.state !== 'off');
        if (!others.length) return;
        const reader = others[0];
        later(1300, () => {
            db.conversations[c.id] = { ...db.conversations[c.id], readUpTo: { ...db.conversations[c.id].readUpTo, [reader]: new Date().toISOString() } };
            save();
            emit({ type: 'read', conversationId: c.id, userId: reader, at: db.conversations[c.id].readUpTo[reader] });
        });
        const now = Date.now();
        if (lastSimulated[c.id] && now - lastSimulated[c.id] < 45000) return;
        lastSimulated[c.id] = now;
        const replier = sent.mentions && sent.mentions.length ? sent.mentions[0] : others[Math.floor(Math.random() * others.length)];
        const text = sent.text || '';
        const mood = /thank/i.test(text) ? 'thanks' : /\?\s*$/.test(text) ? 'question' : db.people[replier].status.state === 'client' ? 'later' : 'yes';
        if (Math.random() < 0.5 || mood === 'thanks') {
            later(2000, () => {
                const current = findMessage(c.id, sent.id);
                if (current && !current.deletedAt) replaceMessage({ ...current, reactions: toggleReaction(current.reactions, mood === 'thanks' ? '❤️' : '👍', replier) });
            });
            if (mood === 'thanks') return;
        }
        later(2600, () => emit({ type: 'typing', conversationId: c.id, userId: replier, on: true }));
        later(5200, () => {
            emit({ type: 'typing', conversationId: c.id, userId: replier, on: false });
            const pool = REPLIES[mood];
            const reply = {
                id: localId('srv'), conversationId: c.id, authorId: replier, createdAt: new Date().toISOString(),
                text: pool[Math.floor(Math.random() * pool.length)], attachments: [], reactions: {}, mentions: [],
            };
            db.messages[c.id] = (db.messages[c.id] || []).concat(reply);
            save();
            emit({ type: 'message', message: reply });
            touch(c.id, reply.createdAt);
        });
    }

    const append = (message) => {
        db.messages[message.conversationId] = (db.messages[message.conversationId] || []).concat(message);
        save();
        emit({ type: 'message', message });
    };
    const support = createSupportDesk({
        db: () => db, me, emit, later, save, append, findMessage, replaceMessage, touch,
        setConversation: (c) => { db.conversations[c.id] = c; save(); emit({ type: 'conversation', conversation: c }); },
    });

    db.reminders.forEach(armReminder);
    Object.keys(db.messages).forEach((id) => db.messages[id].forEach((m) => { if (m.deliverAt && new Date(m.deliverAt) > new Date()) armDelivery(m); }));

    const api = {
        me: () => ({ ...me }),

        async bootstrap() {
            return JSON.parse(JSON.stringify({ me: { ...me }, people: db.people, conversations: db.conversations, messages: db.messages, reminders: db.reminders, saved: db.saved, tickets: db.tickets, channelOrder: db.channelOrder, quickReplies: db.quickReplies, prefs: db.prefs, clientSettings: db.clientSettings }));
        },

        async remind(conversationId, messageId, dueAt, label) {
            const r = { id: localId('rem'), conversationId, messageId, dueAt, label, createdAt: new Date().toISOString(), done: false, due: false };
            db.reminders = db.reminders.concat(r);
            save();
            armReminder(r);
            return { ...r };
        },

        async updateReminder(id, patch) {
            db.reminders = db.reminders.map((r) => (r.id === id ? { ...r, ...patch } : r));
            const r = db.reminders.find((x) => x.id === id);
            save();
            if (r && patch.dueAt) armReminder(r);
            return r ? { ...r } : null;
        },

        async toggleSaved(conversationId, messageId) {
            const has = db.saved.some((x) => x.messageId === messageId);
            db.saved = has ? db.saved.filter((x) => x.messageId !== messageId)
                : db.saved.concat({ conversationId, messageId, savedAt: new Date().toISOString() });
            save();
            return db.saved.slice();
        },

        async send(conversationId, draft) {
            const c = db.conversations[conversationId];
            if (!c) throw new Error('No such conversation');
            if (options.failNext) { options.failNext = false; throw new Error('Network'); }
            // The text provider refuses texts to a client who replied STOP
            const client = c.space === 'clients' ? db.people[c.clientId] : null;
            if (client && draft.channel === 'sms' && client.reach && client.reach.textsOff) {
                const refused = new Error(`${firstName(client.name)} turned off texts`);
                refused.reason = 'stopped';
                throw refused;
            }
            const message = {
                id: localId('srv'), conversationId, authorId: me.id, createdAt: new Date().toISOString(),
                text: draft.text || '', attachments: draft.attachments || [], card: draft.card || null,
                replyToId: draft.replyToId || null, threadRootId: draft.threadRootId || null,
                reactions: {}, mentions: findMentions(draft.text, db.people), status: 'sent',
                deliverAt: draft.deliverAt || null,
                note: !!draft.note,
                context: draft.context && draft.context.length ? draft.context.slice(0, 4) : undefined,
                forwardedFrom: draft.forwardedFrom || undefined,
                // Client chats: the reply goes back the client's way (the app, or a text or email)
                channel: c.space === 'clients' && !draft.note ? draft.channel || replyChannel(db.people[c.clientId]) : undefined,
                // Stand-in for the server's link unfurl (backend brief, section 7)
                preview: !draft.card && !(draft.attachments && draft.attachments.length) && firstUrl(draft.text) ? linkPreview(firstUrl(draft.text)) || undefined : undefined,
            };
            if (message.deliverAt) armDelivery(message);
            db.messages[conversationId] = (db.messages[conversationId] || []).concat(message);
            db.conversations[conversationId] = { ...c, lastMessageAt: message.threadRootId || message.note ? c.lastMessageAt : message.createdAt, readUpTo: { ...c.readUpTo, [me.id]: message.createdAt } };
            save();
            // Replying in a chat Bailey is answering takes it over: he pauses until handed back
            if (c.space === 'clients' && !message.note && c.bailey && !(c.bailey.state === 'paused' && c.bailey.takenBy === me.id)) {
                later(0, () => setBailey(conversationId, { state: 'paused', takenBy: me.id }, `${firstName(me.name)} took over from Bailey`));
            }
            simulateTeam(message);
            return JSON.parse(JSON.stringify(message));
        },

        async react(conversationId, messageId, emoji) {
            const current = findMessage(conversationId, messageId);
            if (!current) throw new Error('No such message');
            const next = { ...current, reactions: toggleReaction(current.reactions, emoji, me.id) };
            replaceMessage(next);
            return next;
        },

        async edit(conversationId, messageId, text) {
            const current = findMessage(conversationId, messageId);
            if (!current || current.authorId !== me.id) throw new Error('Not yours');
            const next = { ...current, text, editedAt: new Date().toISOString(), mentions: findMentions(text, db.people) };
            replaceMessage(next);
            return next;
        },

        async remove(conversationId, messageId, { moderate = false } = {}) {
            const current = findMessage(conversationId, messageId);
            const c = db.conversations[conversationId];
            const mine = current && current.authorId === me.id;
            // A manager tidying up team chat (the server checks they are one)
            if (!current || (!mine && !(moderate && c && c.space === 'team'))) throw new Error('Not yours');
            const next = { ...current, text: '', attachments: [], card: null, deletedAt: new Date().toISOString(), ...(mine ? {} : { removedBy: me.id }) };
            replaceMessage(next);
            return next;
        },

        async markRead(conversationId, at) {
            const c = db.conversations[conversationId];
            if (!c) return;
            const mark = at || new Date().toISOString();
            if (c.readUpTo[me.id] && c.readUpTo[me.id] >= mark) return;
            db.conversations[conversationId] = { ...c, readUpTo: { ...c.readUpTo, [me.id]: mark } };
            save();
        },

        async setTyping() { /* the stand-in has nobody to tell */ },

        async markUnread(conversationId, at) {
            const c = db.conversations[conversationId];
            if (!c) return;
            db.conversations[conversationId] = { ...c, readUpTo: { ...c.readUpTo, [me.id]: at } };
            save();
        },

        async pinMessage(conversationId, messageId, on) {
            const c = db.conversations[conversationId];
            if (!c) return null;
            const ids = (c.pinnedMessageIds || []).filter((x) => x !== messageId).concat(on ? [messageId] : []);
            db.conversations[conversationId] = { ...c, pinnedMessageIds: ids };
            if (on) {
                const line = { id: localId('srv'), conversationId, authorId: 'system', createdAt: new Date().toISOString(), text: '', attachments: [], reactions: {}, mentions: [], system: { text: `${firstName(me.name)} pinned a message` } };
                db.messages[conversationId] = (db.messages[conversationId] || []).concat(line);
                emit({ type: 'message', message: line });
            }
            save();
            emit({ type: 'conversation', conversation: db.conversations[conversationId] });
            return db.conversations[conversationId];
        },

        /** Done (or open again): the chat leaves (or rejoins) everyone's inbox. */
        async markDone(conversationId, done) {
            const c = db.conversations[conversationId];
            if (!c) return null;
            const handled = done && c.bailey && c.bailey.state === 'needs-you' ? { bailey: { ...c.bailey, state: 'paused', takenBy: me.id } } : {};
            db.conversations[conversationId] = { ...c, ...handled, doneAt: done ? new Date().toISOString() : null };
            const line = { id: localId('srv'), conversationId, authorId: 'system', createdAt: new Date().toISOString(), text: '', attachments: [], reactions: {}, mentions: [],
                system: { text: done ? `${firstName(me.name)} marked this done` : `${firstName(me.name)} opened this again` } };
            db.messages[conversationId] = db.messages[conversationId].concat(line);
            save();
            emit({ type: 'conversation', conversation: db.conversations[conversationId] });
            emit({ type: 'message', message: line });
            return db.conversations[conversationId];
        },

        /** This phone's push token (the real server keeps one per person per device). */
        async registerDevice(token, platform) {
            db.devices = (db.devices || []).filter((d) => d.token !== token).concat({ token, platform, at: new Date().toISOString() });
            save();
            return { ok: true };
        },

        /** Pause Bailey in a client chat: a person answers from here. */
        async takeOver(conversationId) {
            const c = db.conversations[conversationId];
            if (!c || c.space !== 'clients') return c || null;
            return setBailey(conversationId, { state: 'paused', takenBy: me.id }, `${firstName(me.name)} took over from Bailey`);
        },

        /** Bailey answers this client again. */
        async handBack(conversationId) {
            const c = db.conversations[conversationId];
            if (!c || c.space !== 'clients') return c || null;
            return setBailey(conversationId, { state: 'replying', takenBy: null }, `${firstName(me.name)} handed back to Bailey`);
        },

        async assign(conversationId, staffId) {
            const c = db.conversations[conversationId];
            if (!c) return null;
            db.conversations[conversationId] = { ...c, assigneeId: staffId || null };
            const who = staffId ? db.people[staffId] : null;
            const line = { id: localId('srv'), conversationId, authorId: 'system', createdAt: new Date().toISOString(), text: '', attachments: [], reactions: {}, mentions: [],
                system: { text: who ? `${firstName(me.name)} assigned this chat to ${who.name}` : `${firstName(me.name)} unassigned this chat` } };
            db.messages[conversationId] = db.messages[conversationId].concat(line);
            save();
            emit({ type: 'conversation', conversation: db.conversations[conversationId] });
            emit({ type: 'message', message: line });
            return db.conversations[conversationId];
        },

        /** "I'll take it": the booking moves to me (the real backend reassigns it in the diary). */
        async takeCover(conversationId, messageId) {
            if (!coverTaken(conversationId, messageId, me.id)) throw new Error('Already taken');
            return findMessage(conversationId, messageId);
        },

        /** Quick replies are the salon's, shared by the team. */
        async saveQuickReply(reply) {
            const next = { id: reply.id || localId('qr'), title: String(reply.title || '').trim(), text: String(reply.text || '').trim() };
            if (!next.title || !next.text) throw new Error('A quick reply needs a name and the words');
            const has = db.quickReplies.some((r) => r.id === next.id);
            db.quickReplies = has ? db.quickReplies.map((r) => (r.id === next.id ? next : r)) : db.quickReplies.concat(next);
            save();
            return db.quickReplies.slice();
        },

        async deleteQuickReply(id) {
            db.quickReplies = db.quickReplies.filter((r) => r.id !== id);
            save();
            return db.quickReplies.slice();
        },

        /** My notifications: paused until a time, and only while I'm working (from the diary). */
        async updatePrefs(patch) {
            db.prefs = { ...db.prefs, ...patch };
            save();
            return { ...db.prefs };
        },

        /** The salon's client chat settings: the away message. */
        async updateClientSettings(patch) {
            const next = { ...db.clientSettings, ...patch };
            if (next.awayOn && !String(next.awayText || '').trim()) throw new Error('Write the away message first');
            db.clientSettings = next;
            save();
            return { ...db.clientSettings };
        },

        async setChannelOrder(ids) {
            db.channelOrder = ids.slice();
            save();
            return db.channelOrder;
        },

        async saveToProfile(conversationId, messageId, on) {
            const current = findMessage(conversationId, messageId);
            if (!current) return null;
            const next = { ...current, savedToProfile: !!on };
            replaceMessage(next);
            return next;
        },

        async resendAuto(conversationId, messageId, channel) {
            const current = findMessage(conversationId, messageId);
            if (!current || !current.auto) return null;
            const next = { ...current, auto: { ...current.auto, failed: false, channel, status: 'Delivered' } };
            replaceMessage(next);
            return next;
        },

        async updateConversation(conversationId, patch) {
            const c = db.conversations[conversationId];
            if (!c) return null;
            const allowed = {};
            ['pinned', 'muted', 'topic', 'suggestion', 'name', 'notify', 'postBy'].forEach((k) => { if (k in patch) allowed[k] = patch[k]; });
            if ('name' in allowed && c.kind === 'channel') {
                allowed.name = channelName(allowed.name);
                if (!allowed.name) throw new Error('A channel needs a name');
                const taken = Object.keys(db.conversations).some((k) => k !== conversationId && db.conversations[k].kind === 'channel' && db.conversations[k].name === allowed.name);
                if (taken) throw new Error(`There's already a #${allowed.name}`);
            }
            db.conversations[conversationId] = { ...c, ...allowed };
            save();
            emit({ type: 'conversation', conversation: db.conversations[conversationId] });
            return db.conversations[conversationId];
        },

        /** A new channel (everyone at the salon unless members are given) or a group of chosen people. */
        async createChat({ kind, name, topic, memberIds, postBy }) {
            const at = new Date().toISOString();
            const everyone = Object.keys(db.people).filter((pid) => db.people[pid].kind === 'staff');
            const members = Array.from(new Set([me.id].concat(memberIds && memberIds.length ? memberIds : everyone)));
            let tidy = null;
            if (kind === 'channel') {
                tidy = channelName(name);
                if (!tidy) throw new Error('A channel needs a name');
                if (Object.keys(db.conversations).some((k) => db.conversations[k].kind === 'channel' && db.conversations[k].name === tidy)) throw new Error(`There's already a #${tidy}`);
            }
            const id = localId(kind === 'channel' ? 'c' : 'g');
            db.conversations[id] = {
                id, space: 'team', kind: kind === 'channel' ? 'channel' : 'group', memberIds: members, pinned: false, muted: false,
                createdAt: at, lastMessageAt: at, readUpTo: { [me.id]: at }, createdBy: me.id,
                ...(kind === 'channel' && !(memberIds && memberIds.length) ? { everyone: true } : {}),
                ...(kind === 'channel' && postBy === 'managers' ? { postBy } : {}),
                ...(tidy ? { name: tidy, topic: (topic || '').trim() } : name && name.trim() ? { name: name.trim() } : {}),
            };
            db.messages[id] = [{ id: localId('srv'), conversationId: id, authorId: 'system', createdAt: at, text: '', attachments: [], reactions: {}, mentions: [],
                system: { text: kind === 'channel' ? `${firstName(me.name)} made #${tidy}` : `${firstName(me.name)} started this group` } }];
            save();
            emit({ type: 'conversation', conversation: db.conversations[id] });
            emit({ type: 'message', message: db.messages[id][0] });
            return db.conversations[id];
        },

        /** Add people to a channel or group; each gets a line ("Phoebe added Jade and Leah"). */
        async addMembers(conversationId, ids) {
            const c = db.conversations[conversationId];
            if (!c || c.kind === 'dm' || !ids.length) return c || null;
            const added = ids.filter((x) => c.memberIds.indexOf(x) === -1);
            if (!added.length) return c;
            db.conversations[conversationId] = { ...c, memberIds: c.memberIds.concat(added), readUpTo: added.reduce((acc, x) => ({ ...acc, [x]: new Date().toISOString() }), c.readUpTo) };
            const names = added.map((x) => firstName((db.people[x] || {}).name));
            const said = names.length === 1 ? names[0] : `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
            const line = { id: localId('srv'), conversationId, authorId: 'system', createdAt: new Date().toISOString(), text: '', attachments: [], reactions: {}, mentions: [], system: { text: `${firstName(me.name)} added ${said}` } };
            db.messages[conversationId] = (db.messages[conversationId] || []).concat(line);
            save();
            emit({ type: 'conversation', conversation: db.conversations[conversationId] });
            emit({ type: 'message', message: line });
            return db.conversations[conversationId];
        },

        async removeMember(conversationId, personId) {
            const c = db.conversations[conversationId];
            if (!c || c.kind === 'dm' || c.everyone || personId === me.id) return c || null;
            db.conversations[conversationId] = { ...c, memberIds: c.memberIds.filter((x) => x !== personId) };
            const line = { id: localId('srv'), conversationId, authorId: 'system', createdAt: new Date().toISOString(), text: '', attachments: [], reactions: {}, mentions: [], system: { text: `${firstName(me.name)} removed ${firstName((db.people[personId] || {}).name)}` } };
            db.messages[conversationId] = (db.messages[conversationId] || []).concat(line);
            save();
            emit({ type: 'conversation', conversation: db.conversations[conversationId] });
            emit({ type: 'message', message: line });
            return db.conversations[conversationId];
        },

        /** Leave a channel or group: it goes from my list, the others see a line. */
        async leave(conversationId) {
            const c = db.conversations[conversationId];
            if (!c || c.kind === 'dm' || c.everyone) return null;
            db.conversations[conversationId] = { ...c, memberIds: c.memberIds.filter((x) => x !== me.id) };
            const line = { id: localId('srv'), conversationId, authorId: 'system', createdAt: new Date().toISOString(), text: '', attachments: [], reactions: {}, mentions: [], system: { text: `${firstName(me.name)} left` } };
            db.messages[conversationId] = (db.messages[conversationId] || []).concat(line);
            save();
            emit({ type: 'conversation', conversation: db.conversations[conversationId] });
            return db.conversations[conversationId];
        },

        /** The chat with a client: theirs if there is one, else a new one assigned to me. */
        async openClientChat(client) {
            const id = String(client.id);
            const existing = Object.keys(db.conversations).map((k) => db.conversations[k]).find((c) => c.kind === 'client' && c.clientId === id);
            if (existing) return existing;
            if (!db.people[id]) {
                // Started by the salon: most clients don't have the app yet, so replies go by text
                db.people[id] = { id, name: client.name || 'Client', kind: 'client', role: 'Client', status: { state: 'free' }, profile: client.profile || { since: 'recently' }, reach: client.reach || { channel: 'text', to: '07700 900' + String(100 + (id.length * 37) % 900) } };
                emit({ type: 'people', people: { ...db.people } });
            }
            const at = new Date().toISOString();
            const cid = localId('k');
            const team = Object.keys(db.people).filter((pid) => db.people[pid].kind === 'staff');
            db.conversations[cid] = {
                id: cid, space: 'clients', kind: 'client', clientId: id, memberIds: team.concat(id), pinned: false, muted: false,
                createdAt: at, lastMessageAt: at, readUpTo: { [me.id]: at }, assigneeId: me.id, source: 'salon',
            };
            db.messages[cid] = [];
            save();
            emit({ type: 'conversation', conversation: db.conversations[cid] });
            return db.conversations[cid];
        },

        async openDm(personId) {
            const existing = Object.keys(db.conversations).map((k) => db.conversations[k])
                .find((c) => c.kind === 'dm' && c.memberIds.length === 2 && c.memberIds.indexOf(personId) !== -1 && c.memberIds.indexOf(me.id) !== -1);
            if (existing) return existing;
            const id = localId('d');
            const at = new Date().toISOString();
            db.conversations[id] = { id, space: 'team', kind: 'dm', memberIds: [me.id, personId], pinned: false, muted: false, createdAt: at, lastMessageAt: at, readUpTo: { [me.id]: at } };
            db.messages[id] = [];
            save();
            emit({ type: 'conversation', conversation: db.conversations[id] });
            return db.conversations[id];
        },

        supportFeedback: (messageId, helped) => support.feedback(messageId, helped),
        talkToTeam: () => support.talkToTeam(),
        urgentHelp: (context) => support.urgent(context),
        rateTicket: (messageId, rating) => support.rate(messageId, rating),

        subscribe(fn) {
            listeners.add(fn);
            return () => listeners.delete(fn);
        },

        /** Tests and logout: stop every pending timer. */
        dispose() {
            timers.forEach((t) => clearTimeout(t));
            timers.clear();
            listeners.clear();
        },

        /** Tests: play an event as if the server sent it. */
        __emitForTests: (e) => emit(e),

        /** Staging helper: start the demo again from the seed. */
        reset({ fresh = false } = {}) {
            timers.forEach((t) => clearTimeout(t));
            timers.clear();
            db = build(fresh ? freshSeed(me) : seed(me));
            save();
            emit({ type: 'reset' });
        },
    };
    return api;
}
